// src/hooks/useLogQueries.ts
import type {
  ApiProcessResponseType,
  ProcessFiltersType,
  ProcessTypeResponse,
} from "@/types/entities/process";
import axios from "@/utils/axios";
import {
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useEffect } from "react";
import type {
  ProcessWithPublishedFlowResponse,
  CreateProcessPayload,
  CreateProcessDetailsPayload,
  UploadFilePayload,
  UpdateProcessPayload,
  UpdateProcessResponse,
  AssignTagToProcessPayload,
  UpdateStatusProcessPayload,
} from "./process.types";

export { useGetLogs, useGetLog, useDownloadLogs } from "./process.logs.api";
export type {
  LogFilters,
  CauserData,
  LogData,
  ApiLogSingleResponseType,
  ApiLogListResponseType,
  LogQueryOptions,
} from "./process.logs.api";
export type {
  CreateProcessPayload,
  CreateProcessDetailsPayload,
  UploadFilePayload,
  UpdateProcessPayload,
  UpdateProcessResponse,
  AssignTagToProcessPayload,
  UpdateStatusProcessPayload,
} from "./process.types";

export const createProcess = async (payload: CreateProcessPayload) => {
  const { data } = await axios.post("admin/v1/process", payload, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
  return data;
};

export const useCreateProcess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-process"],
    mutationFn: createProcess,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["process"] });
    },
  });
};

export const useCreateProcessDetails = (id: string | undefined) => {
  return useMutation({
    mutationFn: async (payload: CreateProcessDetailsPayload) => {
      const response = await axios.put(`/admin/v1/process/${id}`, payload);
      return response.data;
    },
  });
};

export const useUpdateProcessPrerrequisites = (id: string | undefined) => {
  return useMutation({
    mutationFn: async (payload: {
      prerequisites: {
        id: string;
        order: number;
      }[];
    }) => {
      const response = await axios.post(
        `/admin/v1/process/${id}/prerequisites`,
        payload,
      );
      return response.data;
    },
  });
};

// Servicio para subir un archivo asociado a un proceso (sin respuesta esperada)
export const uploadFile = async ({
  file,
  entity_id,
  entity_type,
  collection,
}: UploadFilePayload): Promise<void> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("entity_id", entity_id);
  formData.append("entity_type", entity_type);
  if (collection) {
    formData.append("collection", collection);
  }
  await axios.post("admin/v1/file", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      Accept: "application/json",
    },
  });
};

export const useGetProcesses = (filters?: ProcessFiltersType) => {
  return useQuery<ApiProcessResponseType>({
    queryKey: ["process-list", filters],
    queryFn: async () => {
      const { data } = await axios.get<ApiProcessResponseType>(
        "/admin/v1/process",
        {
          params: {
            ...filters,
            relationships: filters?.relationships?.join(","),
          },
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return data;
    },
  });
};

export const useGetProcessesAsInfinite = (filters?: ProcessFiltersType) => {
  return useInfiniteQuery({
    queryKey: ["process-list-infinite", filters],
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await axios.get<ApiProcessResponseType>(
        "/admin/v1/process",
        {
          params: {
            page: pageParam,
            per_page: 10,
            order_by: "created_at",
            ...(filters?.filter && { filter: filters.filter }),
            relationships: filters?.relationships?.join(","),
          },
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      return lastPage.data.length === 10 ? nextPage : undefined;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 1,
  });
};

const getProcess = async (id: string, version: number | null) => {
  const { data } = await axios.get<ProcessTypeResponse>(
    `admin/v1/process/${id}`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      params: {
        version,
        relationships: "createdBy,tags,files,flows,prerequisites,tags.files",
      },
    },
  );
  return data;
};

const getProcessWithPublishedFlow = async (id: string) => {
  const { data } = await axios.get<ProcessWithPublishedFlowResponse>(
    `admin/v1/process/${id}`,
    {
      params: { relationships: "publishedFlow" },
    },
  );
  return data;
};

export const useGetProcessWithPublishedFlow = (id?: string) => {
  return useQuery({
    queryKey: ["admin-process-published-flow", id],
    queryFn: () => getProcessWithPublishedFlow(id as string),
    enabled: Boolean(id),
  });
};

export const createProcessDetailsBreadcrumb = (
  queryClient: QueryClient,
  id: string,
  type?: string | null,
) => {
  return async () => {
    const processId = id;
    try {
      const queryKey = type
        ? ["admin-process-detail", processId, type]
        : ["admin-process-detail", processId];

      const process = await queryClient.ensureQueryData({
        queryKey,
        queryFn: async () => getProcess(processId, null),
        staleTime: 5 * 60 * 1000,
      });

      return { data: process.data };
    } catch (error) {
      console.error(
        "Error fetching admin-process-detail for breadcrumb:",
        error,
      );
      const axiosError = error as AxiosError<{ message: string }>;
      throw (
        axiosError ?? {
          code: 500,
          message: "Unknown error",
        }
      );
    }
  };
};

export const useGetProcess = (
  id?: string | number,
  version?: number | null,
) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["admin-process-detail", id, version],
    queryFn: () => getProcess(id as string, version ?? null),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (query.isSuccess) {
      queryClient.invalidateQueries({ queryKey: ["admin-log-list"] });
    }
  }, [query.isSuccess, queryClient]);

  return query;
};

export const updateProcess = async (
  payload: UpdateProcessPayload,
  id: string,
): Promise<UpdateProcessResponse> => {
  const { data } = await axios.patch<UpdateProcessResponse>(
    `admin/v1/process/${id}`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  );
  return data;
};

export const useUpdateProcess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-process"],
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateProcessPayload;
    }) => updateProcess(payload, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-process-detail", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["process"] });
    },
  });
};

export const useDuplicateProcess = () => {
  return useMutation({
    mutationKey: ["duplicate-process"],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload?: UpdateProcessPayload;
    }) => {
      const response = await axios.patch(`/admin/v1/duplicate/${id}`, payload);
      return response.data;
    },
  });
};

// Función para asignar tag a un proceso
export const assignTagToProcess = async (
  payload: AssignTagToProcessPayload,
): Promise<void> => {
  await axios.put("/admin/v1/tag/assignation/assign", payload, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
};

export const unassignTagToProcess = async (
  payload: AssignTagToProcessPayload,
): Promise<void> => {
  await axios.put("/admin/v1/tag/assignation/unassign", payload, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
};

// Hook para asignar tag a un proceso
export const useAssignTagToProcess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["assign-tag-to-process"],
    mutationFn: assignTagToProcess,
    onSuccess: () => {
      // Invalidar queries relacionadas con procesos cuando se asigna un tag
      queryClient.invalidateQueries({ queryKey: ["process"] });
    },
  });
};

export const useUnassignTagToProcess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["unassign-tag-to-process"],
    mutationFn: unassignTagToProcess,
    onSuccess: () => {
      // Invalidar queries relacionadas con procesos cuando se desasigna un tag
      queryClient.invalidateQueries({ queryKey: ["process"] });
    },
  });
};

export const updateStatusProcess = async (
  payload: UpdateProcessPayload,
  id: string,
): Promise<UpdateProcessResponse> => {
  const { data } = await axios.post<UpdateProcessResponse>(
    `admin/v1/status/${id}`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  );
  return data;
};

export const useUpdateStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-status-process"],
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateStatusProcessPayload;
    }) => updateStatusProcess(payload, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-process-detail", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["process"] });
    },
  });
};

export const useArchiveProcess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["archive-process"],
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateStatusProcessPayload;
    }) => updateProcess(payload, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-process-detail", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["process"] });
    },
  });
};
